import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from '../contexts/LayoutContext';
import { useAppDispatch } from '../store';
import { fetchMatterDetails } from '../store/actions/Matters/MatterActions';
import { InsightsTabContent } from '../components/Matters/tabs/Insights2';
import DetailFrame from '../components/shared/Frames/DetailFrame';
import Badge from '../components/shared/TailwindComponents/Badge';
import Dropdown, { DropdownOption } from '../components/shared/TailwindComponents/Dropdown';
import { PiLightbulbLight } from 'react-icons/pi';
import apis from '../utils/apis';
import Spinner from '../components/shared/TailwindComponents/Spinner';

interface MatterListItem {
  uuid: string;
  MatterName: string;
  MatterType?: string;
  Jurisdiction?: string;
}

function StrategyPage() {
  const { t } = useTranslation();
  const { matterId } = useParams<{ matterId?: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { setIsContentScrollable } = useContext(LayoutContext);

  const [matters, setMatters] = useState<MatterListItem[]>([]);
  const [loadingMatters, setLoadingMatters] = useState(true);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(matterId || null);
  const [matterData, setMatterData] = useState<any>(null);
  const [loadingMatter, setLoadingMatter] = useState(false);

  useEffect(() => {
    setIsContentScrollable(true);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  // Fetch matters list on mount
  useEffect(() => {
    const loadMatters = async () => {
      try {
        setLoadingMatters(true);
        const response = await apis.get('/matters/view', {
          params: { page: 1, page_size: 200, sortBy: 'updated_at', sortOrder: 'desc' },
        });
        const mattersList = response.data?.matters || response.data?.data || [];
        setMatters(
          mattersList.map((m: any) => ({
            uuid: m.uuid || m.MatterUUID || m.id,
            MatterName: m.MatterName || m.title || m.name || 'Untitled',
            MatterType: m.MatterType || m.type || '',
            Jurisdiction: m.Jurisdiction || m.jurisdiction || '',
          }))
        );
      } catch (err) {
        console.error('Failed to load matters:', err);
      } finally {
        setLoadingMatters(false);
      }
    };
    loadMatters();
  }, []);

  // Load selected matter's full data
  useEffect(() => {
    if (!selectedMatterId) {
      setMatterData(null);
      return;
    }

    const loadMatterDetails = async () => {
      setLoadingMatter(true);
      try {
        const result = await dispatch(fetchMatterDetails(selectedMatterId));
        if (result) {
          setMatterData({
            ...result,
            uuid: selectedMatterId,
            MatterName: result.MatterName || result.title || t('Untitled matter'),
            Description: result.Description || result.description || '',
            MatterType: result.MatterType || result.type || '',
            Jurisdiction: result.Jurisdiction || result.jurisdiction || '',
          });
        }
      } catch (err) {
        console.error('Failed to load matter details:', err);
      } finally {
        setLoadingMatter(false);
      }
    };

    loadMatterDetails();
  }, [selectedMatterId, dispatch]);

  // Sync route param
  useEffect(() => {
    if (matterId && matterId !== selectedMatterId) {
      setSelectedMatterId(matterId);
    }
  }, [matterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectMatter = useCallback(
    (uuid: string) => {
      setSelectedMatterId(uuid);
      history.replace(`/strategy/${uuid}`);
    },
    [history]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedMatterId(null);
    setMatterData(null);
    history.replace('/strategy');
  }, [history]);

  const matterOptions: DropdownOption[] = useMemo(
    () =>
      matters.map((m) => ({
        value: m.uuid,
        label: m.MatterName,
      })),
    [matters]
  );

  const selectedMatter = useMemo(
    () => matters.find((m) => m.uuid === selectedMatterId),
    [matters, selectedMatterId]
  );

  const handleRefresh = useCallback(() => {
    if (selectedMatterId) {
      setSelectedMatterId((prev) => {
        setMatterData(null);
        return prev;
      });
    }
  }, [selectedMatterId]);

  // Breadcrumbs - Strategy > [Matter Name]
  const breadcrumbs = useMemo(() => {
    const crumbs = [
      { name: t('Strategy'), href: '/strategy', current: !selectedMatterId },
    ];
    if (selectedMatter) {
      crumbs[0].current = false;
      crumbs.push({
        name: selectedMatter.MatterName,
        href: `/strategy/${selectedMatter.uuid}`,
        current: true,
      });
    }
    return crumbs;
  }, [t, selectedMatterId, selectedMatter]);

  // Matter selector dropdown - placed in header
  const handleDropdownChange = useCallback(
    (val: string | string[]) => {
      const uuid = Array.isArray(val) ? val[0] : val;
      if (uuid) {
        handleSelectMatter(uuid);
      } else {
        handleClearSelection();
      }
    },
    [handleSelectMatter, handleClearSelection]
  );

  const additionalHeaderElements = useMemo(
    () => (
      <div className="flex items-center gap-3">
        <Dropdown
          options={matterOptions}
          value={selectedMatterId || ''}
          onChange={handleDropdownChange}
          placeholder={t('Select a matter...')}
          width="w-56"
          compact
        />
        {selectedMatter?.MatterType && (
          <Badge color="gray">{selectedMatter.MatterType}</Badge>
        )}
        {selectedMatter?.Jurisdiction && (
          <Badge color="gray">{selectedMatter.Jurisdiction}</Badge>
        )}
      </div>
    ),
    [matterOptions, selectedMatterId, selectedMatter, t, handleDropdownChange]
  );

  return (
    <>
      <DetailFrame
        breadcrumbs={breadcrumbs}
        dropdownActions={[]}
        additionalHeaderElements={additionalHeaderElements}
        showBackButton={!!selectedMatterId}
        onBack={selectedMatterId ? handleClearSelection : undefined}
      >
        <div className="flex-1 flex flex-col">
          {/* Empty state - no matter selected */}
          {!selectedMatterId && (
            <div className="flex flex-col items-center justify-center flex-1 text-center px-6 py-16">
              <PiLightbulbLight className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
              <h2 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                {t('Select a matter to get started')}
              </h2>
              <p className="text-sm text-gray-400 dark:text-gray-500 max-w-md">
                {t('Choose a matter from the dropdown above to view AI-generated insights, risk assessments, and strategic recommendations.')}
              </p>
            </div>
          )}

          {/* Loading state */}
          {selectedMatterId && loadingMatter && (
            <div className="flex items-center justify-center flex-1 py-16">
              <div className="flex flex-col items-center gap-3">
                <Spinner size="glyph" className="!h-8 !w-8" />
                <span className="text-sm text-gray-500">{t('Loading matter data...')}</span>
              </div>
            </div>
          )}

          {/* Content - insights */}
          {selectedMatterId && !loadingMatter && matterData && (
            <InsightsTabContent
              matterData={matterData}
              onUpdate={handleRefresh}
            />
          )}
        </div>
      </DetailFrame>
    </>
  );
}

export default StrategyPage;
